import {
  OwFApplicationKey,
  OwFFormFieldKey,
  OwFFormFieldType,
} from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/consts/index";
import {
  IOwFFormFieldSchema,
  OwFFormFieldValueType,
} from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/OwFFormField";
import { useIsNMIApplication } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useIsNMIApplication";
import { useStrictLocalization } from "@faire/web/common/localization";
import {
  isNotNull,
  isNotUndefined,
} from "@faire/web/common/typescriptUtils";
import { IDocument } from "@faire/web-api--source/indigofair/data/IDocument";
import { IRetailerLaunchWithFaireApplication } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaireApplication";
import { IRetailerLaunchWithFaireApplicationRequest } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaireApplicationRequest";
import { useIsOptimizedSCC } from "./useIsOptimizedSCC";

export const getVisibleFieldKeys = (
  allFields: OwFFormFieldKey[],
  visibleFields: OwFFormFieldKey[]
) => {
  return allFields.filter((field) => visibleFields.includes(field));
};

export const showFundingSources = ({
  funding_amount_min,
  funding_amount_max,
  funding_amount,
  isSettingOn,
}: Pick<
  IRetailerLaunchWithFaireApplication,
  "funding_amount_min" | "funding_amount_max" | "funding_amount"
> & { isSettingOn?: boolean }): boolean => {
  if (isSettingOn) {
    // Worth noting that funding_amount is being used to store an index of fundingAmountRanges
    // and it has been like that for some time.

    // Hidden by default. Only shows when user selects any option other than `None` for
    // `How much funding have you secured so far?` question.
    return (
      funding_amount !== undefined &&
      (funding_amount_min !== undefined || funding_amount_max !== undefined)
    );
  }

  return (
    (funding_amount_min?.amount_cents ?? 0) !== 0 ||
    (funding_amount_max?.amount_cents ?? 0) !== 0
  );
};

interface IArgs {
  key: OwFFormFieldKey;
  isResidentialAddressFormVisible: boolean;
  setValue: (
    fieldName: OwFFormFieldKey,
    newValue: OwFFormFieldValueType
  ) => void;
  toggleResidentialAddressForm: () => void;
}

export const useBuildResidentialAddressSchema = (): {
  buildResidentialAddressSchema: (data: IArgs) => IOwFFormFieldSchema;
} => {
  const { strictLocalize } = useStrictLocalization();
  const { isNMIApplication } = useIsNMIApplication();
  const isOptimizedSCC = useIsOptimizedSCC();

  const buildResidentialAddressSchema = ({
    key,
    isResidentialAddressFormVisible,
    setValue,
    toggleResidentialAddressForm,
  }: IArgs): IOwFFormFieldSchema => ({
    fieldType: isResidentialAddressFormVisible
      ? OwFFormFieldType.ADDRESS_FORM
      : OwFFormFieldType.RESIDENTIAL_ADDRESS_AUTOCOMPLETE,
    fieldName: "residential_address",
    placeholder: isNMIApplication || isOptimizedSCC
      ? strictLocalize({
          defaultMessage: "Search for your address",
          description: {
            text: "Question in Open with Faire application form",
            img: "https://cdn.faire.com/fastly/179f2cec47af5d044351f8c2eac0eed7b93b5c1c4de8a9cd6f1213eed791cb10.png",
          },
        })
      : undefined,
    labelText: isNMIApplication || isOptimizedSCC
      ? strictLocalize({
          defaultMessage: "Residential address",
          description: {
            text: "Question in Open with Faire application form",
            img: "https://cdn.faire.com/fastly/179f2cec47af5d044351f8c2eac0eed7b93b5c1c4de8a9cd6f1213eed791cb10.png",
          },
        })
      : strictLocalize({
          defaultMessage: "What's your residential address?",
          description: {
            text: "Question in Open with Faire application form",
          },
        }),
    required: true,
    onChange: (value: OwFFormFieldValueType) => {
      if (isResidentialAddressFormVisible) {
        setValue("residential_address_google_place_id", undefined);
      }
      setValue(key, value);
    },
    onSelectAddress: (value) => {
      setValue("residential_address_google_place_id", value);
    },
    toggleAddressForm: toggleResidentialAddressForm,
  });

  return { buildResidentialAddressSchema };
};

export const formDocumentToken = (docs: IDocument[] | undefined): string[] =>
  docs && docs.length > 0
    ? docs.map((doc) => doc.token ?? null).filter(isNotNull)
    : [];

export const attachDocumentTokens = (
  formData: Readonly<IRetailerLaunchWithFaireApplication>,
  keys: OwFApplicationKey[],
  visibleKeys: OwFFormFieldKey[]
): Partial<IRetailerLaunchWithFaireApplicationRequest> => {
  return keys
    .map((key) =>
      visibleKeys.includes(key) && key.includes("documents")
        ? {
            [key.replace(
              "documents",
              "document_tokens"
            ) as keyof IRetailerLaunchWithFaireApplicationRequest]:
              formDocumentToken(formData[key] as IDocument[]),
          }
        : undefined
    )
    .filter(isNotUndefined)
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});
};
